// src/components/loginforms/VerificationPrompt.js
import React, { useState, useEffect } from 'react';
import styles from '../Header.module.css';
import { useAuth } from '../../context/AuthProvider';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';

const VerificationPrompt = () => {
  const { 
    verificationEmail, 
    handleVerificationPrompt, 
    loading, 
    error: authError, 
    verificationSuccess,
    setVerificationSuccess,
    setError 
  } = useAuth();
  
  const [sendingEmail, setSendingEmail] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [localError, setLocalError] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showSignInForm, setShowSignInForm] = useState(false);
  
  // Initialize email input with verification email if available
  useEffect(() => {
    if (verificationEmail) {
      setEmailInput(verificationEmail);
    }
  }, [verificationEmail]);
  
  // Handle automatic redirection after success
  useEffect(() => {
    if (verificationSuccess && !countdown) {
      // Start countdown from 8 seconds
      setCountdown(8);
    }
  }, [verificationSuccess, countdown]);
  
  // Countdown timer effect
  useEffect(() => {
    if (countdown === null) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    
    // When countdown reaches 0, we'll redirect in the AuthProvider
  }, [countdown]);
  
  // Direct send attempt using temporarily stored password
  const handleYesClick = async () => {
    setSendingEmail(true);
    setLocalError('');
    
    const tempPassword = localStorage.getItem('tempSignupPassword');
    
    if (tempPassword) {
      // We have the password from signup, try to send verification email directly
      try {
        // Sign in the user
        const userCredential = await signInWithEmailAndPassword(auth, verificationEmail, tempPassword);
        
        // Send verification email
        await sendEmailVerification(userCredential.user, {
          url: 'https://linkist.se/custom-auth-action',
          handleCodeInApp: true,
        });
        
        // Sign them out again
        await signOut(auth);
        
        // Clear the temporary password
        localStorage.removeItem('tempSignupPassword');
        
        // Show success message
        setVerificationSuccess(true);
        
      } catch (error) {
        console.error("Error sending verification email:", error);
        setLocalError("Could not send verification email automatically. Please provide your password.");
        setShowSignInForm(true);
      }
    } else {
      // No stored password, show the sign-in form
      setShowSignInForm(true);
    }
    
    setSendingEmail(false);
  };
  
  // Handle manual sign-in and verification email sending
  const handleManualSend = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    setLocalError('');
    
    try {
      // Sign in the user
      const userCredential = await signInWithEmailAndPassword(auth, emailInput, passwordInput);
      
      // Send verification email
      await sendEmailVerification(userCredential.user, {
        url: 'https://linkist.se/custom-auth-action',
        handleCodeInApp: true,
      });
      
      // Sign them out again
      await signOut(auth);
      
      // Show success message
      setVerificationSuccess(true);
      setShowSignInForm(false);
      
    } catch (error) {
      console.error("Error during manual verification:", error);
      
      if (error.code === 'auth/wrong-password') {
        setLocalError("Incorrect password. Please try again.");
      } else if (error.code === 'auth/user-not-found') {
        setLocalError("No account found with this email.");
      } else if (error.code === 'auth/too-many-requests') {
        setLocalError("Too many attempts. Please try again later.");
      } else {
        setLocalError("Error: " + error.message);
      }
    }
    
    setSendingEmail(false);
  };
  
  const handleNoClick = async () => {
    await handleVerificationPrompt(false);
  };
  
  return (
    <div className={styles.verificationPrompt}>
      <h3>Email Verification Required</h3>
      
      <p>
        To use Linkist.se, you must verify your email address <strong>{verificationEmail || "your account"}</strong>.
      </p>
      
      {verificationSuccess ? (
        <div className={styles.successMessage}>
          <p>Verification email has been sent! Please check your inbox.</p>
          <p>You will be redirected to the sign-in page in {countdown} seconds...</p>
        </div>
      ) : showSignInForm ? (
        <div className={styles.manualVerificationForm}>
          <p>Please enter your credentials to send a verification email:</p>
          
          {localError && <p className={styles.errorText}>{localError}</p>}
          {authError && <p className={styles.errorText}>{authError}</p>}
          
          <form onSubmit={handleManualSend}>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Email address"
              required
            />
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Password"
              required
            />
            <div className={styles.verificationActions}>
              <button 
                type="submit"
                className={styles.resendButton}
                disabled={loading || sendingEmail}
              >
                {sendingEmail ? 'Sending...' : 'Send Verification Email'}
              </button>
              
              <button 
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowSignInForm(false)}
                disabled={loading || sendingEmail}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <p>Would you like to send a verification email now?</p>
          
          {localError && <p className={styles.errorText}>{localError}</p>}
          {authError && <p className={styles.errorText}>{authError}</p>}
          
          <div className={styles.verificationActions}>
            <button 
              className={styles.resendButton}
              onClick={handleYesClick}
              disabled={loading || sendingEmail}
            >
              {sendingEmail ? 'Sending...' : 'Yes, send verification email'}
            </button>
            
            <button 
              className={styles.cancelButton}
              onClick={handleNoClick}
              disabled={loading || sendingEmail}
            >
              No, take me back
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VerificationPrompt;